import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { FormIds } from '../utils/enums/FormIds.enum';
import { HourSlot, InsuranceDTO } from '@utils/interfaces';
import { GenericObj, getInsuranceName, mergeObjects } from '@utils/functions';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { useOnlineBookingStore } from './onlineBookingStore';

export interface AppointmentTypeInfo {
  appointmentType: string;
  idTranslation?: string;
  clBeforeAnswer?: string;
  eyeExamWithUsAnswer?: boolean;
  name: string;
  isModified?: boolean;
}

export interface InsuranceInfo {
  plan: string;
  id?: string;
  other?: string;
  type?: string;
  memberId?: string;
  holder?: string;
  isModified?: boolean;
  name?: string;
  secondaryInsurance?: {
    //TODO: fatto questa modifica, discuterne
    id?: string;
    memberId?: string;
    holder?: string;
    name?: string;
    type?: string;
  };
}

export enum ChooseNewSlotType {
  BOTH,
  INSURANCE,
  APPOINTMENT,
}

export interface ChooseNewSlot {
  type?: ChooseNewSlotType;
  ruleTime?: string;
  ruleUnit?: string;
}

export interface ScheduleInfo {
  day?: Date;
  doctor: string;
  hour: string;
  slotId: HourSlot;
}

export interface ExtraInfoScheduleExam {
  timer?: string;
  timeTab?: number;
  chooseNewSlot?: ChooseNewSlot;
  expired?: boolean;
}

export interface PhoneInfo {
  prefix?: string;
  phoneNumber?: number;
  type?: 'mobile' | 'landline';
}

export interface PrimaryInsuranceHolder {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  dob: string;
  address: string;
  city: string;
  postalCode?: string;
  state?: string;
  country?: string;
  relationshipToCurrentUser: string;
}

export type CustomFieldToSend = Array<{
  question: string;
  answer: string;
  id?: string;
}>;

export interface PatientDetailsInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phoneNumber: PhoneInfo;
  streetAddress: string;
  city: string;
  gender?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  visionInsuranceDetails?: {
    visionInsurance?: string;
    visionInsurancePlan?: string;
    visionInsuranceMemberId?: string;
    visionInsuranceHolder?: string;
    sameAsForMedicalInsurance?: boolean;
    otherInsuranceName?: string;
    firstName?: string;
    lastName?: string;
    gender?: string;
    dateOfBith?: string;
    streetAddress?: string;
    city?: string;
    zipCode?: string;
    state?: string;
    country?: string;
    visionInsuranceHolders?: string;
  };
  medicalInsuranceDetails?: {
    medicalInsurancePlan?: string;
    medicalInsurance?: string;
    medicalInsuranceMemberId?: string;
    medicalInsuranceHolder?: string;
    sameAsForVisionInsurance?: boolean;
    firstName?: string;
    lastName?: string;
    gender?: string;
    dateOfBith?: string;
    streetAddress?: string;
    city?: string;
    zipCode?: string;
    state?: string;
    country?: string;
    medicalInsuranceHolders?: string;
  };
  storeCustomFieldsPatient?: GenericObj;
  storeCustomFieldsInsurance?: GenericObj;
  customFieldsPatient: CustomFieldToSend;
  customFieldsInsurance: CustomFieldToSend;
}

export interface ContactDetailsInfo {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: PhoneInfo;
  storeCustomFieldsMarketing?: GenericObj;
  customFieldsMarketing: CustomFieldToSend;
}

export interface Patient {
  appointmentId?: string;
  appointmentType: AppointmentTypeInfo;
  insurance: InsuranceInfo;
  scheduleExam?: ScheduleInfo;
  extraInfoScheduleExam?: ExtraInfoScheduleExam;
  patientDetails: PatientDetailsInfo;
  storeCustomFieldsCustomPage: GenericObj;
  customFieldsCustomPage: CustomFieldToSend;
  reservationId?: string;
  order?: number;
  patientId?: string; // serve sul giro reschedule
  previousSlotToDisable?: ScheduleInfo;
}

export interface PatientStore {
  patients: Array<Patient | undefined>;
  // failedPatients?: Array<Patient | undefined>;
  contactsDetails: ContactDetailsInfo;
  updatePatient: (
    currPatient: number,
    sectionKey: FormIds,
    newData: any,
    insurances?: InsuranceDTO,
    previousSlotToDisable?: ScheduleInfo, // <-- optionally
  ) => void;
  deletePatient: (patientIndex: number) => void;
  setAppointmentId: (appId: string, patientIndex: number) => void;
  // setFailedPatients: (failedPatients: Array<Patient | undefined>) => void;
  showDeletePatientModal: boolean;
  updateContactDetils: (newData: ContactDetailsInfo) => void;
  updateReservationId: (
    patientIndex: number,
    newReservationId: string | undefined,
  ) => void;
  importPatientDetailsData: () => void;
  setIsModified: (
    patientIndex: number,
    type: 'insurance' | 'appointment' | 'both',
    newValue: boolean,
  ) => void;
  setPatientOrder: (index: number, val: number) => void;
  setShowDeletePatientModal: (show: boolean) => void;
  reset: () => void;
  resetScheduleExam: (currentPatient: number) => void;
  setTimerScheduleExam: (
    currentPatient: number,
    timer: string | undefined,
  ) => void;
  setTimerExpired: (currentPatient: number) => void;
  setTimeTabScheduleExam: (
    currentPatient: number,
    timeTab: number | undefined,
  ) => void;
  setChooseNewSlotScheduleExam: (
    currentPatient: number,
    chooseNewSlot: ChooseNewSlot | undefined,
  ) => void;
  getCurrentPatient: (currentPatient: number) => Patient | undefined;
  getPatients: () => Array<Patient | undefined>;
  getContactsDetails: () => ContactDetailsInfo;
  getStore: () => PatientStore;
  previousSlotToDisable?: ScheduleInfo;
}

const initialState = {
  patients: [undefined, undefined, undefined],
  contactsDetails: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    customFieldsMarketing: [],
    storeCustomFieldsMarketing: {
      field1: false,
      field2: false,
      field3: false,
    },
  } as ContactDetailsInfo,
  showDeletePatientModal: false,
  isModifyingForUnmount: false,
  previousSlotToDisable: undefined,
};

export const usePatientStore = create(
  persist<PatientStore>(
    (set, get) => ({
      ...initialState,

      getPatients: () => get().patients,
      getContactsDetails: () => get().contactsDetails,
      getStore: () => get(),

      resetScheduleExam: currentPatient => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            patient.scheduleExam = undefined;
            if (patient.extraInfoScheduleExam?.timeTab) {
              patient.extraInfoScheduleExam.timeTab = undefined;
            }
          }
          return { ...tempState };
        });
      },

      setPatientOrder: (index: number, newOrder: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[index]) {
            const patient = tempState.patients[index];
            patient.order = newOrder;
          }
          return { ...tempState };
        });
      },

      setTimerScheduleExam: (currentPatient, timer) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              patient.extraInfoScheduleExam.timer = timer;
              patient.extraInfoScheduleExam.expired = false;
            } else {
              patient.extraInfoScheduleExam = {
                timer: timer,
                expired: false,
              };
            }
          }
          return { ...tempState };
        });
      },

      setTimerExpired: (currentPatient: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              patient.extraInfoScheduleExam.expired = true;
            } else {
              patient.extraInfoScheduleExam = {
                expired: true,
              };
            }
          }
          return { ...tempState };
        });
      },

      setTimeTabScheduleExam: (currentPatient, timeTab) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              patient.extraInfoScheduleExam.timeTab = timeTab;
            } else {
              patient.extraInfoScheduleExam = {
                timeTab,
              };
            }
          }
          return { ...tempState };
        });
      },

      setAppointmentId: (appId: string, index: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          const patients = tempState.patients.map((p, i) => {
            if (i === index) {
              return {
                ...p,
                appointmentId: appId,
              } as Patient;
            } else {
              return p;
            }
          });
          return { ...tempState, patients: patients };
        });
      },

      setChooseNewSlotScheduleExam: (currentPatient, chooseNewSlot) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (patient && patient.extraInfoScheduleExam) {
              if (
                patient.extraInfoScheduleExam.chooseNewSlot &&
                chooseNewSlot
              ) {
                patient.extraInfoScheduleExam.chooseNewSlot = Object.assign(
                  patient.extraInfoScheduleExam.chooseNewSlot,
                  chooseNewSlot,
                );
              } else {
                patient.extraInfoScheduleExam.chooseNewSlot = chooseNewSlot;
              }
            } else if (patient) {
              patient.extraInfoScheduleExam = {
                chooseNewSlot,
              };
            } else {
              console.log(
                '###### da capire casistica: ',
                patient,
                chooseNewSlot,
              );
            }
          }
          return { ...tempState };
        });
      },

      setIsModified: (currentPatient, type, newValue) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            if (type === 'appointment' && patient.appointmentType) {
              patient.appointmentType.isModified = newValue;
            } else if (type === 'insurance' && patient.insurance) {
              patient.insurance.isModified = newValue;
            } else if (patient.insurance && patient.appointmentType) {
              patient.insurance.isModified = newValue;
              patient.appointmentType.isModified = newValue;
            }
          }
          return { ...tempState };
        });
      },

      setIsModifyingForUnmount: (isMod: boolean) => {
        set(state => {
          return {
            ...state,
            isModifyingForUnmount: isMod,
          };
        });
      },

      updateContactDetils: (newData: ContactDetailsInfo) => {
        set(state => {
          console.log('#new contact details data', newData);
          return {
            ...state,
            contactsDetails: newData,
          };
        });
      },

      updateReservationId: (
        currentPatient: number,
        newReservationId: string | undefined,
      ) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          if (tempState.patients[currentPatient]) {
            const patient = tempState.patients[currentPatient];
            patient.reservationId = newReservationId;
          }
          return { ...tempState };
        });
      },

      updatePatient: (
        currentPatient,
        sectionKey,
        newData,
        insurances?: InsuranceDTO,
      ) => {
        set(state => {
          console.log(
            '#currentPatient.update: ',
            newData,
            currentPatient,
            sectionKey,
          );
          if (
            !state.patients[currentPatient] &&
            sectionKey !== FormIds.AppointmentType
          ) {
            return state;
          }

          const tempState = JSON.parse(JSON.stringify(state));
          // In caso di nuovo paziente lo aggiungo direttamente.
          if (
            currentPatient < 0 ||
            currentPatient >= tempState.patients.length
          ) {
            const newPatient = { ...newData }; // Crea un nuovo paziente con i dati forniti

            return {
              ...tempState,
              patients: [...tempState.patients, newPatient], // Crea un nuovo array con il nuovo paziente
            };
          }

          // Creiamo il nuovo oggetto di patient
          const patientToUpdate = mergeObjects<Patient>(
            tempState.patients[currentPatient],
            newData,
          );

          //se il paziente non ha un order, lo aggiungo
          if (
            !state.patients[currentPatient] &&
            sectionKey === FormIds.AppointmentType
          ) {
            patientToUpdate.order = currentPatient;
          }

          // Controllo specifico per lo step di insurance
          if (insurances) {
            // recupero l'oggettoInsurance e recupero il type corretto.
            const insuranceType =
              sectionKey === FormIds.PatientDetail
                ? tempState.patients[currentPatient].insurance.type
                : insurances.find(
                    i => i.insuranceId.toString() === newData.insurance.id,
                  )?.type;

            const insuranceID = state.patients[currentPatient]?.insurance?.id;
            //Aggiorno lo stato dell'insurance in base alle informazioni che ho aggiunto dentro patient details
            if (
              (sectionKey === FormIds.PatientDetail ||
                sectionKey === FormIds.PatientDetailPrimaryInsurance ||
                sectionKey === FormIds.PatientDetailSecondaryInsurance) &&
              insuranceID &&
              insuranceID !== ''
            ) {
              const patientData = newData.patientDetails as PatientDetailsInfo;
              const visionInsuranceMemberId =
                patientData?.visionInsuranceDetails?.visionInsuranceMemberId;
              const visionInsuranceHolders =
                patientData?.visionInsuranceDetails?.firstName &&
                patientData?.visionInsuranceDetails?.lastName
                  ? `${patientData?.visionInsuranceDetails?.firstName} ${patientData?.visionInsuranceDetails?.lastName}`
                  : '';
              const medicalInsuranceMemberId =
                patientData?.medicalInsuranceDetails?.medicalInsuranceMemberId;
              const medicalInsuranceHolders =
                patientData?.medicalInsuranceDetails?.firstName &&
                patientData?.medicalInsuranceDetails?.lastName
                  ? `${patientData?.medicalInsuranceDetails?.firstName} ${patientData?.medicalInsuranceDetails?.lastName}`
                  : '';
              console.log(
                '#aggiorno holders',
                patientData,
                visionInsuranceHolders,
                medicalInsuranceHolders,
              );
              // in base al tipo setto la secondary insurance con i dati aggiornati
              //"Other" insurance options is considered as Vision, not Medical */}
              if (
                [
                  StaticValues.INSURANCE_TYPE_VISION,
                  StaticValues.INSURANCE_TYPE_OTHER,
                ].includes(insuranceType as StaticValues)
              ) {
                patientToUpdate.insurance = {
                  ...patientToUpdate.insurance,
                  memberId: visionInsuranceMemberId,
                  holder: visionInsuranceHolders,
                  secondaryInsurance: {
                    id: patientData.medicalInsuranceDetails?.medicalInsurance,
                    memberId:
                      patientData.medicalInsuranceDetails
                        ?.medicalInsuranceMemberId,
                    holder:
                      patientData.medicalInsuranceDetails
                        ?.medicalInsuranceHolders,
                    name: getInsuranceName(
                      insurances,
                      patientData?.medicalInsuranceDetails?.medicalInsurance,
                    ),
                    type:
                      patientData.medicalInsuranceDetails?.medicalInsurance !==
                      ''
                        ? StaticValues.INSURANCE_TYPE_MEDICAL
                        : '',
                  },
                };
              } else {
                patientToUpdate.insurance = {
                  ...patientToUpdate.insurance,
                  memberId: medicalInsuranceMemberId,
                  holder: medicalInsuranceHolders,
                  secondaryInsurance: {
                    id: patientData.visionInsuranceDetails?.visionInsurance,
                    memberId:
                      patientData.visionInsuranceDetails
                        ?.visionInsuranceMemberId,
                    holder:
                      patientData.visionInsuranceDetails
                        ?.visionInsuranceHolders,
                    name: getInsuranceName(
                      insurances,
                      patientData?.visionInsuranceDetails?.visionInsurance,
                    ),
                    type:
                      patientData.visionInsuranceDetails?.visionInsurance !== ''
                        ? StaticValues.INSURANCE_TYPE_VISION
                        : '',
                  },
                };
              }
            } else {
              // setto il type
              patientToUpdate.insurance = {
                ...patientToUpdate.insurance,
                type: insuranceType,
                memberId: undefined,
                holder: undefined,
                secondaryInsurance: undefined,
              };

              if (patientToUpdate.patientDetails) {
                // azzero i valori dello step patientDetails per i box insurance detail
                patientToUpdate.patientDetails = {
                  ...patientToUpdate.patientDetails,
                  visionInsuranceDetails: undefined,
                  medicalInsuranceDetails: undefined,
                };
              } else if (patientToUpdate.insurance.id !== '') {
                (patientToUpdate.patientDetails as any) = {};
              }

              // a seconda di cosa ho selezionato imposto già il valore predefinito.
              if (
                [
                  StaticValues.INSURANCE_TYPE_VISION,
                  StaticValues.INSURANCE_TYPE_OTHER,
                ].includes(insuranceType as StaticValues)
              ) {
                patientToUpdate.patientDetails = {
                  ...patientToUpdate.patientDetails,
                  visionInsuranceDetails: {
                    visionInsurance: newData.insurance?.id,
                    otherInsuranceName: newData.insurance?.other || undefined,
                  },
                };
              } else if (
                insuranceType === StaticValues.INSURANCE_TYPE_MEDICAL
              ) {
                patientToUpdate.patientDetails = {
                  ...patientToUpdate.patientDetails,
                  medicalInsuranceDetails: {
                    medicalInsurance: newData.insurance?.id,
                  },
                };
              }
            }
          }

          // Aggiorna l'array dei pazienti con il paziente aggiornato
          tempState.patients[currentPatient] = patientToUpdate;

          // Restituisce il nuovo stato con l'array dei pazienti aggiornato
          return { ...tempState };
        });
      },

      deletePatient: (patientIndex: number) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;

          // Fai una copia dell'array dei pazienti, evitando di modificarlo direttamente
          // const updatedPatients = [...tempState.patients];
          tempState.patients[patientIndex] = undefined;
          // updatedPatients.splice(patientIndex, 1);

          // // Aggiorna lo stato con la nuova lista
          // tempState.patients = updatedPatients;

          // const remainingPatientsCount = updatedPatients.length;
          const onlyTruePatient = tempState.patients.filter(p => p);
          console.log('#deletePatient.onlyTruePatient: ', onlyTruePatient);
          if (onlyTruePatient.length > 0) {
            const findLastIndex = tempState.patients.findLastIndex(p => p);
            if (findLastIndex !== -1) {
              useOnlineBookingStore.getState().setCurrentPatient(findLastIndex);
            } else {
              console.log('## non ci sono più pazienti...');
            }
          } else {
            console.log('## non ci sono più pazienti...');
          }

          // if (remainingPatientsCount > 0) {
          //   const newCurrentPatientIndex = Math.min(
          //     patientIndex,
          //     remainingPatientsCount - 1,
          //   );
          //   useOnlineBookingStore
          //     .getState()
          //     .setCurrentPatient(newCurrentPatientIndex);
          // } else {
          //   useOnlineBookingStore.getState().setCurrentPatient(0);
          // }

          // step 0 utente non creato... => situazione attuale [u,u,u]
          // step 1 utente aggiunto (seleziono appt) => situazione attuale [u,u,u] situazione post [0,u,u]
          // order = 0 -> posizione [0]
          // step 2 utente aggiunto (seleziono appt) => situazione attuale [0,u,u] situazione post [0,1,u]
          // order = 0 -> posizione [0]
          // order = 1 -> posizione [1]
          // step 3 utente cancellato posizione 0 => situazione attuale [0,1,u] situazione post [u,1,u]
          // order = 1 -> posizione [1]
          // step 4 utente aggiunto => situazione attuale [u,1,u] situazione post [0,1,u]
          // mi prendo l'order più grande (della situazione attuale) ... 1, al nuovo utente l'order è ((più grnade) + 1)
          // logica di "spazio libero" più a sinistra => situazione attuale [u,1,u] allora lo "spazio" libero è 0
          // order = 2 -> posizione [0]
          // order = 1 -> posizione [1]

          return { ...tempState };
        });
      },

      importPatientDetailsData: () => {
        set(state => {
          // TODO RIVEDERE
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          const patients = tempState.patients;
          const patientToImport = patients.find(
            p =>
              p?.order ===
              Math.min(
                ...patients.map(obj =>
                  typeof obj?.order !== 'undefined' ? obj.order : 99,
                ),
              ),
          );

          return {
            ...tempState,
            contactsDetails: {
              ...tempState.contactsDetails,
              firstName: patientToImport?.patientDetails?.firstName,
              lastName: patientToImport?.patientDetails?.lastName,
              phoneNumber: patientToImport?.patientDetails?.phoneNumber,
              email: patientToImport?.patientDetails?.email,
              dateOfBirth: patientToImport?.patientDetails?.dateOfBirth,
            },
          };
          // return { ...tempState };
        });
      },

      setShowDeletePatientModal: (show: boolean) => {
        set(state => {
          const tempState = JSON.parse(JSON.stringify(state)) as PatientStore;
          tempState.showDeletePatientModal = show;
          return { ...tempState };
        });
      },

      getCurrentPatient: (currentPatient: number) => {
        return get().patients[currentPatient];
      },

      // Funzione per resettare lo stato
      reset: () => set(initialState),
    }),
    {
      name: 'patientProgression',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
